import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { Flight, Leg } from '@flugwettrennen/shared';
import { hhmm } from '../lib/format';
import { FlightTime } from './FlightTime';
import { RouteMap } from './RouteMap';

/** Großkreis-Distanz in km (Haversine) – für die Detailanzeige. */
function distanceKm(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return Math.round(2 * 6371 * Math.asin(Math.min(1, Math.sqrt(h))));
}

export function FlightDetails({ flight, onClose }: { flight: Flight; onClose: () => void }) {
  const { t } = useTranslation();

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Pseudo-Leg für die Kartendarstellung (Ursprung → Ziel).
  const leg: Leg = {
    order: 1,
    flightNumber: flight.flightNumber,
    airlineName: flight.airlineName,
    airlineIata: flight.airlineIata,
    aircraftType: flight.aircraftType,
    from: flight.origin,
    to: flight.destination,
    scheduledDeparture: flight.scheduledDeparture,
    actualDeparture: flight.scheduledDeparture,
    scheduledArrival: flight.scheduledArrival,
    actualArrival: flight.scheduledArrival,
  };

  const hasCoords =
    (flight.origin.lat !== 0 || flight.origin.lon !== 0) &&
    (flight.destination.lat !== 0 || flight.destination.lon !== 0);
  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="flight-details">
        <div className="modal-head">
          <span className="mono" style={{ fontWeight: 700, fontSize: '1.1rem' }}>{flight.flightNumber}</span>
          <span className={`pill ${flight.category === 'passenger' ? 'pill-go' : 'pill-stop'}`}>
            {t(`game.category.${flight.category}`)}
          </span>
          <button className="btn btn-ghost btn-sm close" onClick={onClose} aria-label={t('game.close')}>✕</button>
        </div>
        <div className="modal-body">
          <div className="detail-grid">
            <div>
              <div className="k">{t('live.from')}</div>
              <div className="v">{flight.origin.iata} · {flight.origin.name}</div>
            </div>
            <div>
              <div className="k">{t('live.to')}</div>
              <div className="v">{flight.destination.iata} · {flight.destination.name}</div>
            </div>
            <div>
              <div className="k">{t('live.departure')}</div>
              <div className="v">
                <FlightTime
                  scheduled={flight.scheduledDeparture}
                  estimated={flight.estimatedDeparture}
                  delayMinutes={flight.delayMinutes}
                />
              </div>
            </div>
            <div>
              <div className="k">{t('live.arrival')}</div>
              <div className="v">
                <FlightTime scheduled={flight.scheduledArrival} estimated={flight.estimatedArrival} />
              </div>
            </div>
            <div>
              <div className="k">Airline</div>
              <div className="v">{flight.airlineName}</div>
            </div>
            <div>
              <div className="k">{t('game.aircraftTypeLabel')}</div>
              <div className="v">{flight.aircraftType ?? '—'}</div>
            </div>
            <div>
              <div className="k">{t('game.category_label')}</div>
              <div className="v">{t(`game.category.${flight.category}`)}</div>
            </div>
            <div>
              <div className="k">{t('game.distance')}</div>
              <div className="v">{hasCoords ? `${distanceKm(flight.origin, flight.destination)} km` : '—'}</div>
            </div>
          </div>

          {hasCoords && (
            <RouteMap
              legs={[leg]}
              start={{ iata: flight.origin.iata, name: flight.origin.name, lat: flight.origin.lat, lon: flight.origin.lon }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
